import { z } from 'zod';

export interface ClinicalTemplate {
  id: number;
  name: string;
  description: string | null;
  template_content?: string | null;
  example_note?: string | null;
  custom_prompt?: string | null;
  auth_org_id?: string | null;
  source_template_id?: number | null;
  source_template_scope?: string | null;
  visibility_labels?: string[];
  is_default?: boolean;
  auto_copy_for_new_users?: boolean;
  updated_at?: string | null;
}

export interface ClinicalTemplateFormState {
  name: string;
  description: string;
  template_content: string;
  example_note: string;
  custom_prompt: string;
  visibility_labels: string;
  is_default: boolean;
  auto_copy_for_new_users: boolean;
}

export interface TodoCatalogTemplate {
  id: number;
  todo_type: string;
  template_name: string;
  system_message: string;
  field_specifications?: unknown;
  is_active: boolean;
}

export interface TodoCatalogFormState {
  todo_type: string;
  template_name: string;
  system_message: string;
  field_specifications: string;
  is_active: boolean;
}

export interface ClinicalTemplatePayload {
  name: string;
  description: string | null;
  template_content: string;
  example_note: string | null;
  custom_prompt: string | null;
  visibility_labels?: string[];
  is_default?: boolean;
  auto_copy_for_new_users?: boolean;
}

export interface TodoCatalogPayload {
  todo_type?: string;
  template_name: string;
  system_message: string;
  field_specifications: unknown | null;
  is_active: boolean;
}

export interface TodoAssignment {
  id: number;
  auth_org_id: string;
  todo_type: string;
  todo_template_catalog_id: number;
  is_active: boolean;
  labels?: string[];
  template?: Pick<TodoCatalogTemplate, 'id' | 'todo_type' | 'template_name'>;
  catalog_template?: Pick<TodoCatalogTemplate, 'id' | 'todo_type' | 'template_name'>;
}

export const emptyClinicalTemplateForm: ClinicalTemplateFormState = {
  name: '',
  description: '',
  template_content: '',
  example_note: '',
  custom_prompt: '',
  visibility_labels: '',
  is_default: true,
  auto_copy_for_new_users: false,
};

export const emptyTodoCatalogForm: TodoCatalogFormState = {
  todo_type: '',
  template_name: '',
  system_message: '',
  field_specifications: '',
  is_active: true,
};

const requiredTrimmedString = (message: string) => z.string().refine((value) => value.trim().length > 0, message);

export const clinicalTemplateFormSchema = z.object({
  name: requiredTrimmedString('Nafn er nauðsynlegt.'),
  description: z.string(),
  template_content: requiredTrimmedString('Innihald sniðmáts er nauðsynlegt.'),
  example_note: z.string(),
  custom_prompt: z.string(),
  visibility_labels: z.string(),
  is_default: z.boolean(),
  auto_copy_for_new_users: z.boolean(),
});

const jsonTextSchema = z.string().refine((value) => {
  if (!value.trim()) return true;
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}, 'Reitaskilgreiningar þurfa að vera gilt JSON.');

export const todoCatalogFormSchema = (isEditing: boolean) =>
  z.object({
    todo_type: isEditing ? z.string() : requiredTrimmedString('Tegund verkefnis er nauðsynleg.'),
    template_name: requiredTrimmedString('Nafn er nauðsynlegt.'),
    system_message: requiredTrimmedString('Kerfisboð eru nauðsynleg.'),
    field_specifications: jsonTextSchema,
    is_active: z.boolean(),
  });

export async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }
  return response.json();
}

const normalizeVisibilityLabel = (label: string) => label.trim().toLowerCase();

export const labelsFromText = (value: string) => value.split(',').map(normalizeVisibilityLabel).filter(Boolean);

export const labelsToText = (labels: string[] | undefined) => (labels || []).join(', ');

export const visibilityLabelOptionsFromTemplates = (
  templates: Pick<ClinicalTemplate, 'visibility_labels'>[],
  currentValue = ''
) => {
  const labels = new Set(labelsFromText(currentValue));
  for (const template of templates) {
    for (const label of template.visibility_labels || []) {
      const normalizedLabel = normalizeVisibilityLabel(label);
      if (normalizedLabel) labels.add(normalizedLabel);
    }
  }
  return Array.from(labels).sort();
};

export const fieldSpecificationsToText = (value: unknown) => (value ? JSON.stringify(value, null, 2) : '');

export const formFromTemplate = (
  template: ClinicalTemplate,
  overrides: Partial<ClinicalTemplateFormState> = {}
): ClinicalTemplateFormState => ({
  ...emptyClinicalTemplateForm,
  name: template.name,
  description: template.description || '',
  template_content: template.template_content || '',
  example_note: template.example_note || '',
  custom_prompt: template.custom_prompt || '',
  visibility_labels: labelsToText(template.visibility_labels),
  is_default: template.is_default !== false,
  auto_copy_for_new_users: Boolean(template.auto_copy_for_new_users),
  ...overrides,
});

export const clinicalTemplatePayloadFromForm = (
  form: ClinicalTemplateFormState,
  options: { includeVisibilityLabels?: boolean; includeGlobalFlags?: boolean } = {}
): ClinicalTemplatePayload => {
  const payload: ClinicalTemplatePayload = {
    name: form.name.trim(),
    description: form.description.trim() || null,
    template_content: form.template_content,
    example_note: form.example_note || null,
    custom_prompt: form.custom_prompt || null,
  };

  if (options.includeVisibilityLabels) {
    payload.visibility_labels = labelsFromText(form.visibility_labels);
  }

  if (options.includeGlobalFlags) {
    payload.is_default = form.is_default;
    payload.auto_copy_for_new_users = form.auto_copy_for_new_users;
  }

  return payload;
};

export const todoCatalogFormFromTemplate = (template?: TodoCatalogTemplate | null): TodoCatalogFormState =>
  template
    ? {
        todo_type: template.todo_type,
        template_name: template.template_name,
        system_message: template.system_message,
        field_specifications: fieldSpecificationsToText(template.field_specifications),
        is_active: template.is_active,
      }
    : emptyTodoCatalogForm;

export const todoCatalogPayloadFromForm = (form: TodoCatalogFormState, isEditing: boolean): TodoCatalogPayload => ({
  ...(!isEditing ? { todo_type: form.todo_type.trim() } : {}),
  template_name: form.template_name.trim(),
  system_message: form.system_message,
  field_specifications: form.field_specifications.trim() ? JSON.parse(form.field_specifications) : null,
  is_active: form.is_active,
});
